import type { JSX } from "react";
import { useState } from "react";

import { DeleteWithConfirmationDialog } from "@impero-com/common/components/deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { FormSection } from "@impero-com/common/components/formSection/formSection";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import createIcon from "@impero-com/common/img/create-icon.svg";
import exportIcon from "@impero-com/common/img/export.svg";
import reportsIcon from "@impero-com/common/img/reports.svg";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { notifyRequestError } from "@impero-com/common/utils/query_client";
import { ActionSpec, Button, InformationModal } from "@impero-com/ui";
import { useNavigate, useParams } from "react-router-dom";

import type { LegacyControlProgramViewModelWithUserMap } from "api/legacy/model/legacyControlProgramViewModelWithUserMap";
import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import activityIcon from "img/activity.svg";
import deleteIcon from "img/delete.svg";
import duplicateIcon from "img/duplicate.svg";
import editIcon from "img/edit.svg";
import { buildControlActivitiesListUrl } from "org/controlActivities/urlUtils";
import { WebRoute } from "routes";
import { useScrollToLocationHash } from "utils/hooks/useScrollToLocationHash";

import { ControlsPanel } from "./ControlsPanel";
import { DataSheetsPanel } from "./DataSheetsPanel";
import { ExportProgramDialog } from "./ExportProgramDialog";
import { MetadataPanel } from "./MetadataPanel";
import {
    fetchDataSheetGroupings,
    useArchiveDataSheetMutation,
    useControlProgramDetailQuery,
    useCopyControlProgramMutation,
    useDeleteControlMutation,
    useDeleteControlProgramMutation,
} from "./queries";
import { UsersPanel } from "./UsersPanel";
import { affectedControlTitles, formatLegacyUserName } from "./utils";

type Dialog =
    | { type: "DELETE_PROGRAM" }
    | { type: "COPY_PROGRAM" }
    | { type: "EXPORT_PROGRAM" }
    | { type: "DELETE_CONTROL"; controlId: string }
    | {
          type: "DELETE_DATASHEET";
          dataSheetId: string;
          affectedTitles: string[];
      }
    | undefined;

export function ControlProgramDetailPage(): JSX.Element {
    const { id } = useParams();
    const controlProgramId = Number(id);

    if (!id || Number.isNaN(controlProgramId)) {
        return <LoggedInHTTPError code={404} />;
    }

    return (
        <ControlProgramDetail
            key={controlProgramId}
            controlProgramId={controlProgramId}
        />
    );
}

interface ControlProgramDetailProps {
    controlProgramId: number;
}

function ControlProgramDetail({
    controlProgramId,
}: ControlProgramDetailProps): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: model } = useControlProgramDetailQuery(controlProgramId);

    useScrollToLocationHash();

    const [dialog, setDialog] = useState<Dialog>(undefined);

    const deleteProgram = useDeleteControlProgramMutation();
    const copyProgram = useCopyControlProgramMutation();
    const deleteControl = useDeleteControlMutation(controlProgramId);
    const archiveDataSheet = useArchiveDataSheetMutation(controlProgramId);

    const closeDialog = () => setDialog(undefined);

    const userName = (userId: number | null | undefined): string =>
        userId === null || userId === undefined
            ? ""
            : formatLegacyUserName(model.Users[userId]);

    const handleDeleteProgram = () => {
        closeDialog();
        deleteProgram.mutate(
            { controlProgramId },
            { onSuccess: () => navigate(WebRoute.CONTROLS) }
        );
    };

    const handleCopyProgram = () => {
        closeDialog();
        copyProgram.mutate(
            { params: { control_program_id: controlProgramId } },
            {
                onSuccess: (response) =>
                    navigate(
                        `${WebRoute.CONTROL_PROGRAMS}/${response.data.id}/view`
                    ),
            }
        );
    };

    const handleDeleteControl = (controlId: string) => {
        closeDialog();
        deleteControl.mutate({ controlId });
    };

    const handleRequestDeleteDataSheet = async (dataSheetId: string) => {
        try {
            const groupings = await fetchDataSheetGroupings(dataSheetId);
            setDialog({
                type: "DELETE_DATASHEET",
                dataSheetId,
                affectedTitles: affectedControlTitles(groupings),
            });
        } catch (error) {
            notifyRequestError(error);
        }
    };

    const handleConfirmDeleteDataSheet = (dataSheetId: string) => {
        closeDialog();
        archiveDataSheet.mutate({ dataSheetId });
    };

    const actions = useControlProgramActions(
        model,
        controlProgramId,
        setDialog
    );

    return (
        <OrgApplicationBar
            title={{
                title: model.Name,
                breadCrumbs: [
                    {
                        label: t("ControlManagement_ModuleTitle"),
                        url: WebRoute.CONTROLS,
                    },
                ],
            }}
            actions={{ id: "control-program-actions", actions }}
        >
            <FormSection id="control-program-header" titleLabel={model.Name}>
                <MetadataPanel model={model} userName={userName} />
            </FormSection>

            <ControlsPanel
                model={model}
                userName={userName}
                onDeleteControl={(controlId) =>
                    setDialog({ type: "DELETE_CONTROL", controlId })
                }
            />

            <FormSection
                id="control-program-datasheets"
                titleLabel={t("ViewControlProgram_DataSheets")}
            >
                <DataSheetsPanel
                    dataSheets={model.DataSheets}
                    canEdit={model.CanEdit}
                    controlProgramId={controlProgramId}
                    onRequestDelete={(dataSheetId) =>
                        void handleRequestDeleteDataSheet(dataSheetId)
                    }
                />
            </FormSection>

            <FormSection
                id="control-program-users"
                titleLabel={t("ViewControlProgram_Users_Title")}
            >
                <UsersPanel model={model} userName={userName} />
            </FormSection>

            <DeleteWithConfirmationDialog
                open={dialog?.type === "DELETE_PROGRAM"}
                title={t("ViewControlProgram_Delete_ConfirmTitle")}
                onConfirm={handleDeleteProgram}
                onCancel={closeDialog}
            >
                {t("ViewControlProgram_Delete_ConfirmBody")}
            </DeleteWithConfirmationDialog>

            {dialog?.type === "EXPORT_PROGRAM" && (
                <ExportProgramDialog
                    controlProgramId={controlProgramId}
                    onClose={closeDialog}
                />
            )}

            <InformationModal
                open={dialog?.type === "COPY_PROGRAM"}
                onClose={closeDialog}
                type="info"
                icon="duplicate"
                title={t("ViewControlProgram_Copy_Dialog_Title")}
                footer={
                    <>
                        <Button variant="link" onClick={closeDialog}>
                            {t("Cancel")}
                        </Button>
                        <Button variant="primary" onClick={handleCopyProgram}>
                            {t("Ok")}
                        </Button>
                    </>
                }
            >
                {t("ViewControlProgram_Copy_Dialog_Body")}
            </InformationModal>

            {dialog?.type === "DELETE_CONTROL" && (
                <InformationModal
                    open
                    onClose={closeDialog}
                    type="error"
                    icon="delete"
                    title={t("ViewControlProgram_Delete_Control_ConfirmTitle")}
                    footer={
                        <>
                            <Button variant="link" onClick={closeDialog}>
                                {t("Cancel")}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() =>
                                    handleDeleteControl(dialog.controlId)
                                }
                            >
                                {t("Ok")}
                            </Button>
                        </>
                    }
                >
                    {t("ViewControlProgram_Delete_Control_ConfirmBody")}
                </InformationModal>
            )}

            {dialog?.type === "DELETE_DATASHEET" && (
                <InformationModal
                    open
                    onClose={closeDialog}
                    type="error"
                    icon="delete"
                    title={t("DataSheet_Warning_Title")}
                    footer={
                        <>
                            <Button variant="link" onClick={closeDialog}>
                                {t("Cancel")}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() =>
                                    handleConfirmDeleteDataSheet(
                                        dialog.dataSheetId
                                    )
                                }
                            >
                                {t("Ok")}
                            </Button>
                        </>
                    }
                >
                    {dialog.affectedTitles.length > 0
                        ? `${t("DataSheet_Warnings_DeletionWillAffectControls")} ${dialog.affectedTitles.join(", ")}`
                        : t("DataSheet_Warnings_DeletionUnused")}
                </InformationModal>
            )}
        </OrgApplicationBar>
    );
}

function useControlProgramActions(
    model: LegacyControlProgramViewModelWithUserMap,
    controlProgramId: number,
    setDialog: (dialog: Dialog) => void
): ActionSpec[] {
    const { t } = useTranslation();

    return [
        ...(model.CanCreateControl
            ? [
                  {
                      id: "action-add-control",
                      label: t("ViewControlProgram_Add_Control"),
                      icon: { svg: createIcon },
                      link: {
                          to: `${WebRoute.CONTROLS_NEW}?control_program_id=${controlProgramId}`,
                      },
                  },
              ]
            : []),
        ...(model.CanEdit
            ? [
                  {
                      id: "action-edit",
                      label: t("ViewControlProgram_Edit"),
                      icon: { svg: editIcon },
                      link: {
                          to: `${WebRoute.CONTROL_PROGRAMS}/${controlProgramId}`,
                      },
                  },
              ]
            : []),
        ...(model.CanDelete
            ? [
                  {
                      id: "action-delete",
                      label: t("ViewControlProgram_Delete"),
                      icon: { svg: deleteIcon },
                      onClick: () => setDialog({ type: "DELETE_PROGRAM" }),
                  },
              ]
            : []),
        {
            id: "action-export",
            label: t("ControlProgram_Export"),
            icon: { svg: exportIcon },
            onClick: () => setDialog({ type: "EXPORT_PROGRAM" }),
        },
        ...(model.CanCopy
            ? [
                  {
                      id: "action-copy",
                      label: t("ViewControlProgram_Copy"),
                      icon: { svg: duplicateIcon },
                      onClick: () => setDialog({ type: "COPY_PROGRAM" }),
                  },
              ]
            : []),
        {
            id: "action-goto-report",
            label: t("ViewControlProgram_GoToReport"),
            icon: { svg: reportsIcon },
            link: { href: `/api/control-programs/${controlProgramId}/report` },
        },
        {
            id: "action-view-results",
            label: t("ViewControlProgram_ViewResults"),
            icon: { svg: activityIcon },
            link: {
                to: buildControlActivitiesListUrl({
                    type: "controlProgram",
                    controlProgramId,
                }),
            },
        },
    ];
}
